import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextField, MenuItem, Select, InputLabel } from "@mui/material";
import {
  getDoc,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  collection,
  deleteDoc,
} from "firebase/firestore";
import { auth, db, Timestamp } from "../firebase";
import { parseISO, isValid, format } from "date-fns";
import xMarkIcon from "../assets/icons/xmark.svg";
import backArrorIcon from "../assets/icons/arrow_back.svg";
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/textStyles.css";
import "../styles/buttonStyles.css";

interface Patient {
  id: string;
  title: string;
  first_name: string;
  last_name: string;
  dob: Date;
  sex: string;
  email: string;
  phone: string;
  phone_type: string;
  clinical_info: string;
}

interface PatientsTabContentProps {
  uid: string;
}

const PatientsTabContent: React.FC<PatientsTabContentProps> = ({ uid }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showClinikoUploadModal, setShowClinikoUploadModal] =
    useState<boolean>(false);
  const [clinikoApiKey, setClinikoApiKey] = useState<string>("");
  const [showAddPatientModal, setShowAddPatientModal] =
    useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dob, setDob] = useState<Date | null>(null);
  const [sex, setSex] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [phoneType, setPhoneType] = useState<string>("Mobile");
  const [clinicalInfo, setClinicalInfo] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showPatientInfoModal, setShowPatientInfoModal] =
    useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [summaryDocuments, setSummaryDocuments] = useState<
    { id: string; data: any }[]
  >([]);
  const [copiedIndexes, setCopiedIndexes] = useState(new Set());

  const fetchPatients = useCallback(async () => {
    if (!uid) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://europe-west2-motics-consultations.cloudfunctions.net/cliniko-fetch-patients",
        {
          uid,
          // params: { uid }
        }
      );
      const userRef = doc(db, "users", uid);
      const patientsCollectionRef = collection(userRef, "patients");
      const patientsSnapshot = await getDocs(patientsCollectionRef);

      const existingPatients: Patient[] = patientsSnapshot.docs.map((doc) => ({
        id: doc.id, // Include the document ID
        ...doc.data(),
      })) as Patient[];

      const newPatients: Patient[] = (response.data.patients || []).map((patient: any) => ({
        ...patient,
        dob: new Date(patient.date_of_birth),
        phone: patient.patient_phone_numbers.length > 0 ? patient.patient_phone_numbers[0].normalized_number : "",
        phone_type: patient.patient_phone_numbers.length > 0 ? patient.patient_phone_numbers[0].phone_type : "",
      }));
      const mergedPatients = mergePatients(existingPatients, newPatients);
      setPatients(mergedPatients);
    } catch (error) {
      console.error("Error fetching patients from cloud functions:", error);
      // Handle the error gracefully by fetching patients from the patientsCollectionRef
      const userRef = doc(db, "users", uid);
      const patientsCollectionRef = collection(userRef, "patients");
      const patientsSnapshot = await getDocs(patientsCollectionRef);
      const existingPatients: Patient[] = patientsSnapshot.docs.map((doc) => ({
        id: doc.id, // Include the document ID
        ...doc.data(),
      })) as Patient[];
      setPatients(existingPatients);
    }
    setIsLoading(false);
  }, [uid]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const fetchSummaryDocuments = async () => {
    if (selectedPatient) {
      try {
        const patientNotesCollectionRef = collection(
          db,
          `users/${uid}/patients/${selectedPatient.id}`,
          "patientNotes"
        );
        const querySnapshot = await getDocs(patientNotesCollectionRef);
        const newSummaryDocuments: { id: string; data: any }[] = [];
        querySnapshot.forEach((doc) => {
          newSummaryDocuments.push({ id: doc.id, data: doc.data() });
        });
        setSummaryDocuments(newSummaryDocuments);
        // Now you can set the summaryDocuments state or use it as needed
        console.log("Fetched summary documents:", summaryDocuments);
      } catch (error) {
        console.error("Error fetching summary documents:", error);
      }
    }
  };

  // Call the fetchSummaryDocuments function when needed, such as when the selected patient changes
  useEffect(() => {
    fetchSummaryDocuments();
  }, [selectedPatient, uid]);

  const formatDOB = (dob: Date | Timestamp | null): string => {
    if (!dob) return "Not Provided";

    let date: Date;

    if (dob instanceof Timestamp) {
      date = dob.toDate();
    } else if (dob instanceof Date) {
      date = dob;
    } else {
      return "Not Provided";
    }

    return format(date, "dd/MM/yyyy"); // Make sure to import 'format' from 'date-fns'
  };

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCopyToClipboard = (summary: string, index: number) => {
    const element = document.createElement("textarea");
    element.value = summary.replace(/<[^>]+>/g, ""); // Remove HTML tags from the summary
    document.body.appendChild(element);
    element.select();
    navigator.clipboard.writeText(element.value);
    document.body.removeChild(element);

    setCopiedIndexes((prev) => new Set(prev).add(index));
    setTimeout(() => {
      setCopiedIndexes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }, 1000);
  };

  const handleFieldChange = (field: keyof Patient, value: any) => {
    setSelectedPatient((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleSaveEditedPatient = async () => {
    if (selectedPatient) {
      // Update patient in database
      console.log("selectedPatient", selectedPatient.id);
      try {
        const patientDocRef = doc(
          db,
          `users/${uid}/patients`,
          selectedPatient.id
        );
        await updateDoc(patientDocRef, { ...selectedPatient });
        alert("Patient information updated successfully.");
        fetchPatients(); // Refresh the patients list
      } catch (error) {
        console.error("Error updating patient:", error);
        alert("Failed to update patient. Please try again.");
      }
    }
    setIsEditing(false);
  };

  const handleDeletePatient = async () => {
    if (!selectedPatient) return;
  
    const patientDocRef = doc(db, `users/${uid}/patients`, selectedPatient.id);
    
    try {
      // Reference to the patientNotes subcollection
      const patientNotesCollectionRef = collection(patientDocRef, "patientNotes");
  
      // Fetch all documents in the patientNotes subcollection
      const querySnapshot = await getDocs(patientNotesCollectionRef);
  
      // Delete each document in the subcollection
      for (const doc of querySnapshot.docs) {
        await deleteDoc(doc.ref);
      }
  
      // After deleting all documents in the subcollection, delete the patient document
      await deleteDoc(patientDocRef);
  
      alert("Patient deleted successfully.");
      fetchPatients(); // Refresh the patients list
    } catch (error) {
      console.error("Error deleting patient:", error);
      alert("Failed to delete patient. Please try again.");
    }
  
    setSelectedPatient(null);
  };
  

  function mergePatients(existingPatients: Patient[], newPatients: Patient[]) {
    const merged = new Map();
    existingPatients.concat(newPatients).forEach((patient) => {
      merged.set(patient.id, patient);
    });
    return Array.from(merged.values());
  }

  const handleAddPatient = () => {
    setShowAddPatientModal(true);
  };

  const handleAddPatientModalClose = () => {
    setShowAddPatientModal(false);
  };

  const handleSavePatient = async () => {
    // Check for required fields
    if (!firstName || !lastName || !dob || !sex || (!email && !phone)) {
      let missingFields = [];
      if (!firstName) missingFields.push("First Name");
      if (!lastName) missingFields.push("Last Name");
      if (!dob) missingFields.push("Date of Birth");
      if (!sex) missingFields.push("Sex");
      if (!email && !phone) missingFields.push("Email or Phone Number");

      alert(
        `Please fill in the following fields: ${missingFields.join(", ")}.`
      );
      return;
    }

    const newPatient = {
      title: title || "",
      first_name: firstName || "",
      last_name: lastName || "",
      dob: new Date(dob),
      sex: sex || "",
      email: email || "",
      phone: phone || "",
      phone_type: phoneType || "",
      clinical_info: clinicalInfo || "",
    };

    try {
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);
      console.log("uid at patient tab", uid);
      if (userDoc.exists()) {
        console.log("userDoc at patient tab", userDoc);
        const patientsCollectionRef = collection(userRef, "patients");
        await addDoc(patientsCollectionRef, newPatient);
        const userData = userDoc.data();
        if (userData.clinikoApiKey) {
          // Show modal if Cliniko API key exists
          setShowClinikoUploadModal(true);
        } else {
          // Show alert if no Cliniko API key
          alert("Patient successfully added.");
        }
      } else {
        alert("Failed to add patient. Please try again.");
      }
      setShowAddPatientModal(false);
      fetchPatients(); // Refresh the patients list
    } catch (error) {
      console.error("Error adding patient:", error);
      alert("Failed to add patient. Please try again.");
    }
  };

  const handleUploadToCliniko = () => {
    // Logic to upload patient to Cliniko
    console.log("Uploading patient to Cliniko...");
    setShowClinikoUploadModal(false);
  };

  const renderPatientInfo = () => (
    <div>
      <button
        className="medium-icon-button"
        onClick={() => setSelectedPatient(null)}
      >
        <img src={backArrorIcon} alt="back" />
      </button>
      {selectedPatient && (
        <div>
          <h2>Edit Patient</h2>
          <InputLabel id="title-select-label">Title</InputLabel>
          <Select
            labelId="title-select-label"
            value={selectedPatient.title}
            onChange={(e) => handleFieldChange("title", e.target.value)}
            style={{ width: "10rem" }}
            disabled={!isEditing}
          >
            <MenuItem value="Mr.">Mr.</MenuItem>
            <MenuItem value="Mrs.">Mrs.</MenuItem>
            <MenuItem value="Ms.">Ms.</MenuItem>
            <MenuItem value="Miss">Miss</MenuItem>
            <MenuItem value="Dr.">Dr.</MenuItem>
            <MenuItem value="Prof.">Prof.</MenuItem>
            <MenuItem value="Rev.">Rev.</MenuItem>
            <MenuItem value="Sir">Sir</MenuItem>
            <MenuItem value="Lady">Lady</MenuItem>
            <MenuItem value="Lord">Lord</MenuItem>
            <MenuItem value="Dame">Dame</MenuItem>
            <MenuItem value="Mx.">Mx.</MenuItem>
          </Select>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
            <TextField
              label="First Name"
              value={selectedPatient.first_name}
              onChange={(e) => handleFieldChange("first_name", e.target.value)}
              margin="normal"
              fullWidth
              disabled={!isEditing}
            />
            <TextField
              label="Last Name"
              value={selectedPatient.last_name}
              onChange={(e) => handleFieldChange("last_name", e.target.value)}
              margin="normal"
              fullWidth
              disabled={!isEditing}
            />
          </div>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <DatePicker
  label="Date of Birth"
  value={selectedPatient ? selectedPatient.dob : null}
  onChange={(newDate) => handleFieldChange("dob", newDate)}
  disabled={!isEditing}
/>
            <InputLabel id="sex-select-label">Sex</InputLabel>
            <Select
              labelId="sex-select-label"
              value={selectedPatient.sex}
              label="Sex"
              onChange={(e) => handleFieldChange("sex", e.target.value)}
              disabled={!isEditing}
              style={{ width: "10rem" }}
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <TextField
              label="Email"
              type="email"
              margin="normal"
              value={selectedPatient.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              fullWidth
              disabled={!isEditing}
            />
          </div>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
            <TextField
              label="Phone"
              value={selectedPatient.phone}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              fullWidth
              disabled={!isEditing}
            />
            <InputLabel id="phone-type-select-label" style={{ width: "7rem" }}>
              Phone Type
            </InputLabel>
            <Select
              labelId="phone-type-select-label"
              value={selectedPatient.phone_type}
              label="Phone Type"
              onChange={(e) => handleFieldChange("phone_type", e.target.value)}
              disabled={!isEditing}
            >
              <MenuItem value="Mobile">Mobile</MenuItem>
              <MenuItem value="Work">Work</MenuItem>
              <MenuItem value="Home">Home</MenuItem>
              <MenuItem value="Fax">Fax</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </div>
          <TextField
            label="Clinical Information"
            multiline
            rows={4}
            value={selectedPatient.clinical_info}
            onChange={(e) => handleFieldChange("clinical_info", e.target.value)}
            fullWidth
            style={{ marginBottom: "1rem" }}
            disabled={!isEditing}
          />
          {/* Additional Fields */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1rem",
            }}
          >
            <Button onClick={isEditing ? handleSaveEditedPatient : handleEdit}>
              {isEditing ? "Save" : "Edit"}
            </Button>
            {isEditing && <Button onClick={handleDeletePatient}>Delete</Button>}
          </div>
          <hr
            style={{
              margin: "1rem 0",
              border: "0",
              borderTop: "1px solid silver",
            }}
          />
          <h2>Patient Notes</h2>
          {summaryDocuments &&
            summaryDocuments.length > 0 &&
            summaryDocuments
              .sort((a, b) => b.data.timestamp - a.data.timestamp)
              .map((document, index) => (
                <div key={index}>
                  <h4 style={{ marginBottom: "0" }}>
                    {document.data.timestamp.toDate().toLocaleString()}
                  </h4>
                  <div
                    style={{ border: "1px solid silver", padding: "0.5rem" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <h4>Summary</h4>
                      <button
                        className="button-copy2"
                        onClick={() =>
                          handleCopyToClipboard(document.data.summary, index)
                        }
                      >
                        {copiedIndexes.has(index) ? "Copied" : "Copy"}
                      </button>
                    </div>
                    {document.data.clinikoNoteId && (
                      <p>Cliniko Note ID: {document.data.clinikoNoteId}</p>
                    )}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: document.data.summary,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
        </div>
      )}
    </div>
  );

  const renderAddPatientModal = () => (
    <Modal
      open={showAddPatientModal}
      onClose={handleAddPatientModalClose}
      aria-labelledby="add-patient-modal"
      aria-describedby="modal-modal-description"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="add-patients-modal">
          <button
            className="small-icon-button"
            onClick={handleAddPatientModalClose}
          >
            <img src={xMarkIcon} alt="close" />
          </button>
          <div style={{ padding: "0rem 2rem" }}>
            <h2>Add Patient</h2>
            <InputLabel id="title-select-label">Title</InputLabel>
            <Select
              labelId="title-select-label"
              value={title}
              label="Title"
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: "10rem" }}
            >
              <MenuItem value="Mr.">Mr.</MenuItem>
              <MenuItem value="Mrs.">Mrs.</MenuItem>
              <MenuItem value="Ms.">Ms.</MenuItem>
              <MenuItem value="Miss">Miss</MenuItem>
              <MenuItem value="Dr.">Dr.</MenuItem>
              <MenuItem value="Prof.">Prof.</MenuItem>
              <MenuItem value="Rev.">Rev.</MenuItem>
              <MenuItem value="Sir">Sir</MenuItem>
              <MenuItem value="Lady">Lady</MenuItem>
              <MenuItem value="Lord">Lord</MenuItem>
              <MenuItem value="Dame">Dame</MenuItem>
              <MenuItem value="Mx.">Mx.</MenuItem>
            </Select>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
              <TextField
                label="First Name"
                margin="normal"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                margin="normal"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
              <DatePicker
                label="Date of Birth"
                value={dob}
                onChange={(newValue) => setDob(newValue)}
              />

              <InputLabel id="sex-select-label">Sex</InputLabel>
              <Select
                labelId="sex-select-label"
                value={sex}
                label="Sex"
                onChange={(e) => setSex(e.target.value)}
                style={{ width: "10rem" }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <TextField
                label="Email"
                type="email"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
              />
              <InputLabel
                id="phone-type-select-label"
                style={{ width: "7rem" }}
              >
                Phone Type
              </InputLabel>
              <Select
                labelId="phone-type-select-label"
                value={phoneType}
                label="Phone Type"
                onChange={(e) => setPhoneType(e.target.value)}
              >
                <MenuItem value="Mobile">Mobile</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Fax">Fax</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </div>
            <TextField
              label="Clinical Information"
              multiline
              rows={4}
              value={clinicalInfo}
              onChange={(e) => setClinicalInfo(e.target.value)}
              fullWidth
              style={{ marginBottom: "1rem" }}
            />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button variant="contained" onClick={handleSavePatient}>
                Save Patient
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );

  const renderClinikoUploadModal = () => (
    <Modal
      open={showClinikoUploadModal}
      onClose={() => setShowClinikoUploadModal(false)}
      aria-labelledby="cliniko-upload-modal"
      aria-describedby="cliniko-modal-description"
    >
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="reset-password-modal">
          <h2>Patient successfully added</h2>
          <p>Would you like to upload this patient to Cliniko?</p>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "1rem" }}
          >
            <Button onClick={handleUploadToCliniko}>Yes</Button>
            <Button onClick={() => setShowClinikoUploadModal(false)}>No</Button>
          </div>
        </div>
      </div>
    </Modal>
  );

  return (
    <div>
      {!selectedPatient && !isLoading && patients.length > 0 && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2>Patients</h2>
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button variant="contained" onClick={fetchPatients}>
                Sync
              </Button>
              <Button variant="contained" onClick={handleAddPatient}>
                Add Patient
              </Button>
            </div>
          </div>

          <ul>
            {patients.map((patient, index) => {
              console.log("Date of Birth for patient", patient.dob);
              return (
                <div
                  key={index}
                  className="box-sizing-default history-item"
                  onClick={() => handlePatientClick(patient)}
                  style={{
                    cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                  }}
                >
                  <div>
                    <strong>First Name: </strong> {patient.first_name}
                  </div>
                  <div>
                    <strong>Last Name: </strong> {patient.last_name}
                  </div>
                  <div>
                    <strong>Date of Birth: </strong> {formatDOB(patient.dob)}
                  </div>
                </div>
              );
            })}
          </ul>
        </div>
      )}
      {!selectedPatient && isLoading && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2>Patients</h2>
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button variant="contained" onClick={fetchPatients}>
                Sync
              </Button>
              <Button variant="contained" onClick={handleAddPatient}>
                Add Patient
              </Button>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "5rem",
            }}
          >
            <CircularProgress />
          </div>
        </div>
      )}
      {!selectedPatient && !isLoading && patients.length === 0 && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2>Patients</h2>
            <div style={{ display: "flex", gap: "1rem" }}>
              <Button variant="contained" onClick={fetchPatients}>
                Sync
              </Button>
              <Button variant="contained" onClick={handleAddPatient}>
                Add Patient
              </Button>
            </div>
          </div>
          <p>No patients found.</p>
        </div>
      )}
      {selectedPatient && !isLoading && renderPatientInfo()}
      {renderAddPatientModal()}
      {renderClinikoUploadModal()}
    </div>
  );
};

export default PatientsTabContent;
